import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Gift, Check, Sparkles, PenTool } from "lucide-react";

export default function GiftBox() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]); // array of slugs
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const { addGiftBox, openCart } = useCart();
  const { gift_box_price, allowed_cities } = useSettings();
  const GIFT_BOX_PRICE = gift_box_price || 549;
  const zonesText = (allowed_cities || ["Delhi NCR"]).join(" / ");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, []);

  const toggle = (slug) => {
    setSelected((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= 3) {
        toast.info("You can pick 3 pickles for the gift box");
        return prev;
      }
      return [...prev, slug];
    });
  };

  const canAdd = selected.length === 3;

  const addToCart = () => {
    if (!canAdd) {
      toast.error("Pick exactly 3 pickles");
      return;
    }
    const names = products.filter((p) => selected.includes(p.slug)).map((p) => p.name);
    addGiftBox({ selectedSlugs: selected, selectedNames: names, note, price: GIFT_BOX_PRICE });
    toast.success("Gift box added to cart");
    openCart();
    setSelected([]);
    setNote("");
  };

  return (
    <div className="fade-up">
      <section className="bg-gradient-to-b from-[#F4EFE6]/60 to-[#FBF8F3] py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#9E3B21]/10 text-[#9E3B21] text-xs font-semibold uppercase tracking-[0.2em] mb-5">
            <Gift className="w-3.5 h-3.5" /> Gift Box Builder
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E1B18]">Pick 3. Wrap it. Send love.</h1>
          <p className="mt-4 text-[#6E675F] max-w-2xl mx-auto">
            Choose any three of Dadu's homemade pickles (250g each), add a personalized note, and we'll wrap them in a beautiful gift box — perfect for festivals, housewarmings, and long-distance hugs.
          </p>
          <div className="mt-6 inline-flex items-center gap-6 text-sm">
            <div><span className="font-serif text-2xl font-bold text-[#9E3B21]">{formatINR(GIFT_BOX_PRICE)}</span> <span className="text-[#6E675F]">/ box</span></div>
            <div className="text-[#5F7355] font-semibold flex items-center gap-1"><Sparkles className="w-4 h-4" /> Free personalized card</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-semibold">Choose 3 pickles</h2>
              <div className="text-sm font-medium">
                <span className="text-[#9E3B21]" data-testid="gift-selected-count">{selected.length}</span>
                <span className="text-[#6E675F]"> / 3 selected</span>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[1,2,3,4,5,6].map(i => <div key={i} className="h-56 bg-white rounded-2xl animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {products.map((p) => {
                  const isSelected = selected.includes(p.slug);
                  return (
                    <button
                      key={p.slug}
                      data-testid={`gift-select-${p.slug}`}
                      onClick={() => toggle(p.slug)}
                      className={`relative text-left bg-white rounded-2xl border-2 overflow-hidden transition-all ${
                        isSelected
                          ? "border-[#9E3B21] shadow-lg scale-[1.02]"
                          : "border-[#E6DEC3] hover:border-[#D9822B]"
                      }`}
                    >
                      <div className="aspect-square overflow-hidden bg-[#F4EFE6]">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#9E3B21] flex items-center justify-center shadow-md">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="font-serif text-sm font-semibold leading-tight text-[#1E1B18]">{p.name}</p>
                        <p className="text-[10px] text-[#6E675F] uppercase tracking-wider mt-1">{p.spiciness}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-8 bg-white border border-[#E6DEC3] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <PenTool className="w-4 h-4 text-[#9E3B21]" />
                <h3 className="font-serif text-lg font-semibold">Add a personal note (optional)</h3>
              </div>
              <Textarea
                data-testid="gift-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={280}
                placeholder="Happy Diwali, Ma! Missing your rasoi terribly..."
                className="resize-none"
              />
              <p className="text-xs text-[#6E675F] mt-1.5 text-right">{note.length}/280 · Printed on a hand-cut card</p>
            </div>
          </div>

          {/* Summary sticky */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6 sticky top-24">
              <h3 className="font-serif text-xl font-semibold mb-4">Your Gift Box</h3>
              {selected.length === 0 ? (
                <p className="text-sm text-[#6E675F]">Start by picking 3 pickles.</p>
              ) : (
                <ul className="space-y-2 mb-4" data-testid="gift-selected-list">
                  {selected.map((slug) => {
                    const p = products.find((pp) => pp.slug === slug);
                    if (!p) return null;
                    return (
                      <li key={slug} className="flex justify-between text-sm">
                        <span>{p.name}</span>
                        <span className="text-xs text-[#6E675F]">250g</span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="border-t border-[#E6DEC3] pt-4 flex justify-between font-serif text-xl font-semibold">
                <span>Total</span>
                <span>{formatINR(GIFT_BOX_PRICE)}</span>
              </div>
              <p className="text-xs text-[#5F7355] mt-1 text-right">Save ~₹50 vs individual jars</p>
              <Button
                data-testid="gift-add-to-cart"
                disabled={!canAdd}
                onClick={addToCart}
                className="w-full btn-primary rounded-full mt-6 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Gift className="w-4 h-4 mr-2" />
                {canAdd ? "Add Gift Box to Cart" : `Pick ${3 - selected.length} more`}
              </Button>
              <p className="text-xs text-center text-[#6E675F] mt-3">Free delivery on gift boxes in {zonesText}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
